import { useState, useEffect, useRef } from 'react';
import { getDocumentFileToken, buildDocumentFileUrl } from '../services/api';

// Fournit l'URL de streaming d'un PDF pour le viewer (pdf.js charge le document
// page par page via des requetes Range). L'URL embarque un jeton ephemere
// (?ftk=, 30 min, scope-document) car pdf.js ne peut pas injecter de bearer.
//
// Renouvellement transparent : on re-demande un jeton AVANT expiration (a ~83 %
// de la duree de vie, soit ~25 min pour 30 min) et on reconstruit l'URL. Le
// changement d'URL recharge le document avec un jeton frais, sans intervention.
// La fenetre de fuite reste courte (l'ancien jeton expire peu apres).
//
// On marge volontairement : si l'onglet est en arriere-plan, setTimeout peut
// deriver ; un renouvellement a 83 % laisse ~5 min de coussin avant le 403.
const RENEW_RATIO = 0.83;

export function useDocumentFileUrl(documentId: string | undefined): string {
  const [fileUrl, setFileUrl] = useState<string>('');
  // Compteur de "generation" : invalide les renouvellements en vol quand le doc
  // change ou que le composant se demonte (evite un setState tardif).
  const genRef = useRef(0);

  useEffect(() => {
    if (!documentId) {
      setFileUrl('');
      return;
    }

    const gen = ++genRef.current;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loadAndSchedule = async () => {
      try {
        const { token, expiresIn } = await getDocumentFileToken(documentId);
        if (genRef.current !== gen) return; // doc change / unmount entre-temps
        setFileUrl(buildDocumentFileUrl(documentId, token));
        // Reprogramme un renouvellement avant expiration.
        const delayMs = Math.max(30, expiresIn * RENEW_RATIO) * 1000;
        timer = setTimeout(loadAndSchedule, delayMs);
      } catch (err) {
        if (genRef.current === gen) {
          console.error('Failed to load PDF token:', err);
        }
      }
    };

    setFileUrl('');
    loadAndSchedule();

    return () => {
      genRef.current++; // invalide tout renouvellement en vol
      if (timer) clearTimeout(timer);
    };
  }, [documentId]);

  return fileUrl;
}
